import React, { useState, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop } from '@fortawesome/free-solid-svg-icons';

// Configs

import { configs } from '@constants/configs/configs';
import { attestationsConfig } from '@components/ssap/pages/Page3_17';

// Components

import AttestationsInfo from './AttestationsInfo';
import FlagsTooltip from './FlagsTooltip';
import PagesTooltip from './PagesTooltip';
import Taxes from './Taxes';
import IdsInfo from './IdsInfo';
import BloodRelationshipsInfo from './BloodRelationshipsInfo';
import CurrentJsonExtractor from './CurrentJsonExtractor';
import NewJsonSetter from './NewJsonSetter';
import EscPageRenderInfo from './EscPageRenderInfo';
import DataInconsistenciesInfo from './DataInconsistenciesInfo';

// ActionCreators

import { changeData, changePage } from '@actions/ssap/ssapActionCreators';

// Selectors

import {
    selectIfInlineEligibilityIgnoredForEscPage,
    selectIsInlineCurrentIncomeEnabled
} from '@reducers/appReducer';

// Utils

import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';

// Styles

import { darculaManagerStyles } from '../darculaTheme';

const {
    managerRootStyle,
    managerToolbarRowStyle,
    managerToolButtonStyle,
    managerPageCheckerButtonBase,
    managerPageCheckerRunning,
    managerPageCheckerIdle,
} = darculaManagerStyles;

const Manager = props => {

    const {
        actions: {
            changePage
        },
        adjustedFlow,
        bloodRelationshipCodes,
        buttons,
        data,
        data: {
            ssapApplicationId,
            taxHousehold: {
                bloodRelationship,
                primaryTaxFilerPersonId
            }
        },
        householdMembers,
        page,
        relations: {
            loadedRelations
        },
        environment: {
            localDates: {
                serverDate
            },
            stateCode,
            isState
        },
        flow: {
            flowType
        }
    } = props;

    const inlineEligibilityIgnoredForEscPage = useSelector(selectIfInlineEligibilityIgnoredForEscPage);
    const inlineCurrentIncomeEnabled = useSelector(selectIsInlineCurrentIncomeEnabled);

    const [ isChecking, toggleChecking ] = useState(false);
    const [ pageForCheck, getNextPage ] = useState(0);

    const openedItem = localStorageHyperDashboardTools.getValueByPropName('preOpenedMenuItem', 'default');

    const [ state, setState ] = useState(() => {
        const base = { ...buttons };
        if (openedItem && openedItem !== 'default' && Object.prototype.hasOwnProperty.call(base, openedItem)) {
            return { ...base, [openedItem]: true };
        }
        return base;
    });

    const {
        TaxHouseholdComposition,
        Pages,
        Flags,
        Identifiers,
        JsonSetter,
        JsonGetter,
        Relations,
        Attestations,
        EscPageRender,
        DataInconsistencies,
    } = state;

    const handleToggle = ({ target: { name } }) => {
        setState(state => Object
            .keys(state)
            .reduce((newState, btn) => btn === name ? { ...newState, [name]: !state[name] } : { ...newState, [btn]: false }, {}));
    };

    useEffect(() => {
        const tooltipsCloseHandlers = e => {
            if(Object.keys(state).some(key => key === e.target.id)) {
                handleToggle({ target: { name: e.target.id } });
            }
        };
        window.addEventListener('click', tooltipsCloseHandlers);

        return () => window.removeEventListener('click', tooltipsCloseHandlers);
    }, []);

    const [ json, changeJson ] = useState('');
    const [ isApplyJsonButtonDisabled, toggleApplyJsonButtonDisability ] = useState(true);

    const refToJsonTextArea = useRef(null);

    const getButtons = () => {
        return Object.keys(state).map(btnName => {
            return (
                <button
                    key={ btnName }
                    type="button"
                    name={ btnName }
                    onClick={ handleToggle }
                    style={ managerToolButtonStyle }
                >
                    { btnName }
                </button>
            );
        });
    };

    useEffect(() => {
        if(isChecking) {
            setTimeout(() => {
                changePage(pageForCheck);
                getNextPage(p => p + 1 <= adjustedFlow.length - 1 ? p + 1 : 0);
            }, 1000);
        }
    }, [ isChecking, pageForCheck ]);

    const handlePageCheck = () => {
        toggleChecking(isChecking => !isChecking);
    };

    return (
        <div style={ managerRootStyle }>

            <div style={ managerToolbarRowStyle }>
                {
                    getButtons()
                }

                { TaxHouseholdComposition && <Taxes id={'TaxHouseholdComposition'} /> }
                { Flags && <FlagsTooltip id={'Flags'} /> }
                { Pages && <PagesTooltip { ...{ adjustedFlow, id: 'Pages', page, changePage } } /> }
                { Identifiers && <IdsInfo { ...{ id: 'Identifiers', data, ssapApplicationId } } /> }

                {
                    JsonSetter && <NewJsonSetter {...{
                        id: 'JsonSetter',
                        name: JsonSetter,
                        btnText: 'Apply SSAP JSON',
                        appType: configs.routes.SSAP,
                        json,
                        changeJson,
                        isApplyJsonButtonDisabled,
                        toggleApplyJsonButtonDisability,
                        handleToggle,
                        changeData: props.actions.changeData
                    }} />
                }

                {
                    JsonGetter && <CurrentJsonExtractor {...{
                        id: 'JsonGetter',
                        name: JsonGetter,
                        buttons:[
                            ['Copy SSAP JSON as "taxHousehold" === [{ }]', 'array'],
                            ['Copy SSAP JSON as "taxHousehold" === {}', 'object'],
                            ['Copy SSAP JSON for mock file', 'array', true]
                        ],
                        data,
                        refToJsonTextArea,
                        handleToggle
                    }} />
                }

                {
                    Relations && <BloodRelationshipsInfo { ...{
                        id: 'Relations',
                        bloodRelationship,
                        householdMembers,
                        loadedRelations
                    }} />
                }

                {
                    Attestations && <AttestationsInfo { ...{
                        id: 'Attestations',
                        attestationsConfig,
                        flowType,
                        stateCode
                    } } />
                }

                {
                    DataInconsistencies && <DataInconsistenciesInfo id="DataInconsistencies" />
                }

                {
                    EscPageRender && <EscPageRenderInfo { ...{
                        id: 'EscPageRender',
                        bloodRelationship,
                        bloodRelationshipCodes,
                        inlineEligibilityIgnoredForEscPage,
                        inlineCurrentIncomeEnabled,
                        isState,
                        householdMembers,
                        loadedRelations,
                        primaryTaxFilerPersonId,
                        serverDate
                    } } />
                }

            </div>

            <button
                type="button"
                title={ isChecking ? 'Stop cycling pages' : 'Start cycling through pages every second' }
                aria-label={ `Page checker: ${isChecking ? 'on, click to stop' : 'off, click to start'}` }
                aria-pressed={ isChecking }
                style={{
                    ...managerPageCheckerButtonBase,
                    ...(isChecking ? managerPageCheckerRunning : managerPageCheckerIdle),
                }}
                onClick={ handlePageCheck }
            >
                <FontAwesomeIcon
                    icon={ isChecking ? faStop : faPlay }
                    aria-hidden
                    style={{ fontSize: '1.25rem' }}
                />
                { `Checker ${isChecking ? 'Off' : 'On'}` }
            </button>

            
       
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    adjustedFlow: state.ssap.adjustedFlow,
    bloodRelationshipCodes: state.ssap.relations.codes,
    data: state.ssap.data,
    flow: state.ssap.flow,

    householdMembers: state.ssap
        .data
        .taxHousehold
        .householdMember,

    page: state.ssap.page,
    relations: state.ssap.relations
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        changePage
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Manager);